import { useCallback } from "react";
import { useStore } from "react-redux";
import useAppDispatch from "../../../hooks/useAppDispatch";
import useAppSelector from "../../../hooks/useAppSelector";
import type { RootState } from "../../..";
import { useCreateProposalMutation } from "../../../../gql/hooks";
import {
  SetProposalLoading,
  UpdateProposalInfo,
  UpdateProposalInfoField,
} from "..";
import { errorLog } from "../../../../utils/debugLog";

const useProposal = () => {
  const dispatch = useAppDispatch();
  const store = useStore<RootState>();

  const proposalId = useAppSelector((state) => state.proposal.proposalId);

  const [createProposalMutation] = useCreateProposalMutation();

  const createProposal = useCallback(async (): Promise<number | undefined> => {
    dispatch(SetProposalLoading(true));

    try {
      // Read at call time: callers dispatch the point of sale right before calling.
      const { expireDay, name, posId, provinceNumber } =
        store.getState().proposal.proposalInformation;
      const expireAt = expireDay || Math.floor(Date.now() / 1000);

      const response = await createProposalMutation({
        variables: {
          expireAt,
          title: name || "",
          pointOfSaleId: posId || undefined,
          displayedTitle: posId ? undefined : name,
          provinceNumber: provinceNumber,
          isShortProposal: false,
        },
      });

      const id = response.data?.createProposal?.id;
      if (id) {
        dispatch(UpdateProposalInfo({ key: "proposalId", value: id }));
        dispatch(
          UpdateProposalInfoField({ key: "expireDay", value: expireAt }),
        );
      } else {
        errorLog(response.errors, "Proposal created without an id");
      }
      return id;
    } catch (err) {
      errorLog(err, "Issue creating proposal");
      return undefined;
    } finally {
      dispatch(SetProposalLoading(false));
    }
  }, [dispatch, store, createProposalMutation]);

  return { proposalId, createProposal };
};

export default useProposal;
